/**
 * A minimal, safe Markdown parser for the Missions reading surface.
 *
 * The Familiar authors artifacts in Markdown, but the reader used to dump them raw — visible
 * `##`, `**`, and table pipes. This module parses that Markdown into a plain token tree with NO
 * React and NO HTML: `markdown.tsx` walks the tree and renders each node as a React element, so
 * every string lands as auto-escaped text content. There is no `dangerouslySetInnerHTML` path,
 * which is the whole point — an artifact can never inject markup.
 *
 * `[[citation]]` survives as its own inline token so the graph-resolution rule (see
 * `citations.tsx`) still applies inside prose, headings, list items, and table cells.
 *
 * The grammar is deliberately small (headings, lists, tables, blockquotes, fenced code, rules,
 * and inline strong/em/code/cite). Anything it doesn't recognise falls through as literal text —
 * degrade to the raw character, never to broken markup.
 */

export type InlineToken =
  | { kind: 'text'; value: string }
  | { kind: 'code'; value: string }
  | { kind: 'cite'; value: string }
  | { kind: 'strong'; children: InlineToken[] }
  | { kind: 'em'; children: InlineToken[] }

export type Block =
  | { type: 'heading'; level: number; inline: InlineToken[] }
  | { type: 'paragraph'; inline: InlineToken[] }
  | { type: 'code'; lang: string; value: string }
  | { type: 'blockquote'; inline: InlineToken[] }
  | { type: 'list'; ordered: boolean; items: InlineToken[][] }
  | { type: 'table'; header: InlineToken[][]; rows: InlineToken[][][] }
  | { type: 'hr' }

/**
 * Tokenise one line of inline Markdown. Strong/em recurse (so `**[[Name]]**` still resolves the
 * citation); code and cite are leaves. An unmatched delimiter degrades to a literal character.
 */
export function parseInline(text: string): InlineToken[] {
  const tokens: InlineToken[] = []
  let buf = ''
  const flush = () => {
    if (buf) {
      tokens.push({ kind: 'text', value: buf })
      buf = ''
    }
  }
  let i = 0
  while (i < text.length) {
    const two = text.slice(i, i + 2)
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1)
      if (end !== -1) {
        flush()
        tokens.push({ kind: 'code', value: text.slice(i + 1, end) })
        i = end + 1
        continue
      }
    } else if (two === '[[') {
      const end = text.indexOf(']]', i + 2)
      if (end !== -1) {
        flush()
        tokens.push({ kind: 'cite', value: text.slice(i + 2, end) })
        i = end + 2
        continue
      }
    } else if (two === '**') {
      const end = text.indexOf('**', i + 2)
      if (end > i + 2) {
        flush()
        tokens.push({ kind: 'strong', children: parseInline(text.slice(i + 2, end)) })
        i = end + 2
        continue
      }
    } else if (text[i] === '*') {
      const end = text.indexOf('*', i + 1)
      if (end > i + 1) {
        flush()
        tokens.push({ kind: 'em', children: parseInline(text.slice(i + 1, end)) })
        i = end + 1
        continue
      }
    }
    buf += text[i]
    i++
  }
  flush()
  return tokens
}

const HEADING = /^(#{1,6})\s+(.*)$/
const HR = /^(-{3,}|\*{3,}|_{3,})$/
const UL = /^[-*+]\s+(.*)$/
const OL = /^\d+\.\s+(.*)$/
const BLOCKQUOTE = /^>\s?(.*)$/

/** A table separator row: pipes, dashes, colons, spaces — and at least one dash. */
function isTableSeparator(line: string): boolean {
  return /-/.test(line) && /^[\s|:-]+$/.test(line) && line.includes('|')
}

/** Split a `| a | b |` row into trimmed cells, dropping the empties the border pipes create. */
function splitRow(line: string): string[] {
  const cells = line.split('|').map((c) => c.trim())
  if (cells.length && cells[0] === '') cells.shift()
  if (cells.length && cells[cells.length - 1] === '') cells.pop()
  return cells
}

/**
 * Parse a whole Markdown document into blocks. Line-oriented and greedy: consecutive lines of
 * one kind (paragraph text, list items, table rows) coalesce into a single block.
 */
export function parseBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Block[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // blank line — a separator, nothing to emit
    if (line.trim() === '') {
      i++
      continue
    }

    // fenced code block ``` … ```
    const fence = line.match(/^```(.*)$/)
    if (fence) {
      const lang = fence[1].trim()
      const body: string[] = []
      i++
      while (i < lines.length && !/^```/.test(lines[i])) {
        body.push(lines[i])
        i++
      }
      i++ // consume the closing fence (or run off the end)
      blocks.push({ type: 'code', lang, value: body.join('\n') })
      continue
    }

    // horizontal rule
    if (HR.test(line.trim())) {
      blocks.push({ type: 'hr' })
      i++
      continue
    }

    // heading
    const heading = line.match(HEADING)
    if (heading) {
      blocks.push({
        type: 'heading',
        level: heading[1].length,
        inline: parseInline(heading[2].trim()),
      })
      i++
      continue
    }

    // table: a header row of cells followed by a separator row
    if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(line).map(parseInline)
      i += 2 // header + separator
      const rows: InlineToken[][][] = []
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(splitRow(lines[i]).map(parseInline))
        i++
      }
      blocks.push({ type: 'table', header, rows })
      continue
    }

    // blockquote (coalesce consecutive > lines)
    if (BLOCKQUOTE.test(line)) {
      const quoted: string[] = []
      while (i < lines.length && BLOCKQUOTE.test(lines[i])) {
        quoted.push(lines[i].match(BLOCKQUOTE)![1])
        i++
      }
      blocks.push({ type: 'blockquote', inline: parseInline(quoted.join(' ').trim()) })
      continue
    }

    // list (a run of same-ordinality items)
    const ulMatch = line.match(UL)
    const olMatch = line.match(OL)
    if (ulMatch || olMatch) {
      const ordered = Boolean(olMatch)
      const items: InlineToken[][] = []
      while (i < lines.length) {
        const m = ordered ? lines[i].match(OL) : lines[i].match(UL)
        if (!m) break
        items.push(parseInline(m[1].trim()))
        i++
      }
      blocks.push({ type: 'list', ordered, items })
      continue
    }

    // paragraph: gather until a blank line or a line that starts a new block kind
    const para: string[] = []
    while (i < lines.length) {
      const l = lines[i]
      if (
        l.trim() === '' ||
        /^```/.test(l) ||
        HR.test(l.trim()) ||
        HEADING.test(l) ||
        UL.test(l) ||
        OL.test(l) ||
        BLOCKQUOTE.test(l) ||
        (l.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1]))
      ) {
        break
      }
      para.push(l)
      i++
    }
    blocks.push({ type: 'paragraph', inline: parseInline(para.join('\n').trim()) })
  }

  return blocks
}
